import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { layersAtom, setLayersAtom } from '@/atoms/layers.atom';
import { execute } from '@/graphql/execute';
import { layersQuery } from '@/queries/layer.query';

/**
 * Loads the project's layers into `layersAtom` on pages that do not mount the
 * viewer's Layers panel (its `useLayers` is otherwise the atom's only writer).
 * The Analyses page needs them for everything — run input names, Open result,
 * availability of New analysis, re-run — so a refresh or direct link must not
 * depend on the viewer having been open first.
 *
 * Same query key and query as `useLayers`, so it shares that cache (and the
 * progress hooks' invalidations refresh it).
 *
 * Returns `true` once the atom holds THIS project's layers: the atom is global
 * (not keyed by project), so until the seed lands it may still hold the last
 * viewed project's. Identity, not a flag — the atom is set to the very array
 * the query returned.
 *
 * If the fetch fails with nothing cached, `ready` also turns true so the page
 * can render (empty layers) with an error + `refetch`. The atom is cleared
 * first, and `ready` waits for that, so the previously viewed project's layers
 * never leak into this project's page.
 */
export function useProjectLayers(projectId: string | undefined): {
  ready: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const setLayers = useSetAtom(setLayersAtom);
  const atomLayers = useAtomValue(layersAtom);
  const { data, isError, refetch } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId: projectId! }),
    enabled: !!projectId,
  });
  const layers = data?.layers;
  const failed = isError && !layers;
  useEffect(() => {
    if (layers) setLayers(layers);
    else if (failed) setLayers([]);
  }, [layers, failed, setLayers]);
  const ready = (!!layers && atomLayers === layers) || (failed && atomLayers.length === 0);
  return { ready, isError: failed, refetch: () => void refetch() };
}
